import { useEffect, useRef, useState } from "react";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";

// Same loader/key the main app's AfdaniTransport/components/RealMap.tsx
// already uses — calling setOptions again here with the identical key is
// harmless (it's just config for later importLibrary calls, not a live
// connection). No new tracking infrastructure: this renders the exact same
// CurrentLat/CurrentLng/IsOnline data the driver app's background location
// service already writes every ~8s — just surfaced to admins.
setOptions({ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "", v: "weekly" });

const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#0c1428" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b7fa3" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#060b18" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#1a2540" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#0f1e3d" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1a2540" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#3d4f6a" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#08111f" }] },
];

export interface LiveDriverPoint {
  driverId: number;
  name: string;
  lat: number;
  lng: number;
  isOnTrip: boolean;
  vehicleType?: string;
}

function driverDotIcon(isOnTrip: boolean): google.maps.Symbol {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: isOnTrip ? "#4f46e5" : "#00c9a7",
    fillOpacity: 1,
    strokeColor: "#060b18",
    strokeWeight: 2,
  };
}

export default function AdminDriverMap({ drivers, onSelectDriver }: { drivers: LiveDriverPoint[]; onSelectDriver: (driverId: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const fitOnceRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    importLibrary("maps")
      .then(() => {
        if (cancelled || !containerRef.current) return;
        mapRef.current = new google.maps.Map(containerRef.current, {
          center: { lat: 12.9716, lng: 77.5946 },
          zoom: 11,
          disableDefaultUI: true,
          gestureHandling: "greedy",
          styles: DARK_MAP_STYLES,
        });
        setReady(true);
      })
      .catch(() => setError("Google Maps could not be loaded. Check the API key."));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = drivers.map((d) => {
      const marker = new google.maps.Marker({
        map,
        position: { lat: d.lat, lng: d.lng },
        title: `${d.name}${d.isOnTrip ? " (on trip)" : " (online)"}`,
        icon: driverDotIcon(d.isOnTrip),
      });
      marker.addListener("click", () => onSelectDriver(d.driverId));
      return marker;
    });

    // Fit bounds once on first data, then leave the admin's own pan/zoom
    // alone — refitting on every poll would yank the map around underneath
    // whoever's actually looking at it.
    if (!fitOnceRef.current && drivers.length > 0) {
      fitOnceRef.current = true;
      const bounds = new google.maps.LatLngBounds();
      drivers.forEach((d) => bounds.extend({ lat: d.lat, lng: d.lng }));
      map.fitBounds(bounds, 60);
    }
  }, [drivers, ready, onSelectDriver]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#060b18] text-[#6b7fa3] text-sm px-4 text-center">{error}</div>
      )}
      {!ready && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#060b18] text-[#6b7fa3] text-sm">Loading map…</div>
      )}
      {ready && drivers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#060b18]/80 text-[#6b7fa3] text-sm">No drivers online right now</div>
      )}
    </div>
  );
}
